import React, { useCallback, useState, useEffect } from 'react';
import languages from 'language-list';
import * as Yup from 'yup';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { showModal } from '../../../../redux/system/actions';
import ConfirmModal from '../../../common/modals/ConfirmModal';
import Select from '../../../common/Select';

const CANCEL_MODAL_ID = 'cancelModal';

const validationSchema = Yup.object().shape({
  languageName: Yup.string().required('Language name is required'),
  languageRating: Yup.string().required('Language rating is required'),
});

function LanguageForm({ curriculumVitaeId, initialLanguage, setEditingLanguage, setAddingLanguage, onSave, status }) {
  const dispatch = useDispatch();
  const handleShowModal = useCallback((modalId) => dispatch(showModal(modalId)), [dispatch]);

  const [options, setOptions] = useState([]);

  const clearForm = () => {
    setEditingLanguage(null);
  };

  const handleCancel = () => {
    handleShowModal(CANCEL_MODAL_ID);
  };

  const confirmCancel = () => {
    setAddingLanguage(false);
    setEditingLanguage(null);
  };

  useEffect(() => {
    const langData = languages().getData();
    const langOptions = langData.map((lang) => ({
      value: lang.language,
      text: lang.language,
    }));
    setOptions(langOptions);
  }, []);

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={
          initialLanguage || {
            languageName: '',
            languageRating: '',
            curriculumVitaeId,
          }
        }
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm }) => {
          onSave(values);
          clearForm();
          resetForm();
        }}
      >
        {({ errors, touched }) => (
          <Form>
            <div className="row w-50">
              <div className="col-md-12">
                <div className="form-group">
                  <label htmlFor="languageName" className="small">
                    LANGUAGE *
                  </label>
                  <Field
                    as="select"
                    name="languageName"
                    id="languageName"
                    className={`form-control dropdown-language ${touched.languageName && errors.languageName ? 'is-invalid' : ''}`}
                  >
                    <option disabled value="">Select a language</option>
                    {options.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.text}</option>
                    ))}
                  </Field>
                  <ErrorMessage name="languageName">
                    {(msg) => (
                      <div className="invalid-feedback d-block small" style={{ fontStyle: 'italic' }}>
                        {msg}
                      </div>
                    )}
                  </ErrorMessage>
                </div>
              </div>
            </div>

            <div className="row w-50">
              <div className="col-md-12">
                <div className="form-group">
                  <label htmlFor="languageRating" className="small">
                    LANGUAGE RATING *
                  </label>
                  <Field
                    as="select"
                    className={`form-control ${touched.languageRating && errors.languageRating ? 'is-invalid' : ''}`}
                    id="languageRating"
                    name="languageRating"
                  >
                    <option disabled value="">
                      Choose a language rating
                    </option>
                    <option value={0}>Beginner</option>
                    <option value={1}>Intermediate</option>
                    <option value={2}>Proficient</option>
                    <option value={3}>Fluent</option>
                    <option value={4}>Native</option>
                  </Field>
                  <ErrorMessage name="languageRating">
                    {(msg) => (
                      <div className="invalid-feedback d-block small" style={{ fontStyle: 'italic' }}>
                        {msg}
                      </div>
                    )}
                  </ErrorMessage>
                </div>
              </div>
            </div>

            <div className="mb-5">
              {initialLanguage ? (
                <div className="d-flex align-items-start">
                  <button className="btn btn-primary d-flex align-items-center mt-2 mr-3" type="submit">
                    <i className="pe-7s-pen mr-2"></i>
                    <span>Update Language</span>
                  </button>
                  {status === 1 ? (
                    <button className="cancel-button d-flex mt-2" type="button" onClick={handleCancel}>
                      <span>Cancel</span>
                    </button>
                  ) : (
                    <button className="cancel-button d-flex align-items-center mt-2" type="button" onClick={clearForm}>
                      <span>Cancel</span>
                    </button>
                  )}
                </div>
              ) : (
                <div className="d-flex">
                  <button className="btn btn-primary d-flex mt-2 mr-3" type="submit">
                    <i className="pe-7s-plus mr-2"></i>
                    <span>Add Language</span>
                  </button>
                  {status === 1 && (
                    <button className="cancel-button d-flex mt-2" type="button" onClick={handleCancel}>
                      <span>Cancel</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </Form>
        )}
      </Formik>
      <ConfirmModal
        id={CANCEL_MODAL_ID}
        title="Are you sure you want to stop adding this language?"
        message="This will cancel the form and return you to the overview. Do you wish to proceed?"
        onConfirm={confirmCancel}
      />
    </>
  );
}

export default LanguageForm;
